import { BarChart3 } from "lucide-react";
import type React from "react";

export interface GraphStatistics {
	nodes: number;
	edges: number;
	communities: number;
	averageDegree: number;
	density: number;
}

interface StatisticsPanelProps {
	statistics: GraphStatistics;
	visible: boolean;
	onToggle: () => void;
}

/**
 * Statistics Panel component
 * Displays graph metrics including node count, edge count, communities, etc.
 */
export const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ statistics, visible, onToggle }) => {
	if (!visible) {
		return (
			<button
				type="button"
				onClick={onToggle}
				className="surface border border-subtle rounded-lg shadow-sm p-3 mb-4 soft-hover transition-colors flex items-center gap-2"
			>
				<BarChart3 className="w-4 h-4 text-muted" />
				<span className="text-sm font-medium text-muted">Show Stats</span>
			</button>
		);
	}

	return (
		<div className="surface border border-subtle rounded-lg shadow-sm p-4 mb-4">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-2">
					<BarChart3 className="w-5 h-5 text-muted" />
					<h3 className="text-sm font-semibold text-primary">Graph Statistics</h3>
				</div>
				<button type="button" onClick={onToggle} className="text-sm text-subtle hover-text transition-colors">
					Hide
				</button>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="space-y-1 p-3 rounded-md surface-muted border border-strong">
					<div className="text-xs text-subtle">Nodes</div>
					<div className="text-lg font-semibold text-primary">{statistics.nodes}</div>
				</div>

				<div className="space-y-1 p-3 rounded-md surface-muted border border-strong">
					<div className="text-xs text-subtle">Edges</div>
					<div className="text-lg font-semibold text-primary">{statistics.edges}</div>
				</div>

				<div className="space-y-1 p-3 rounded-md surface-muted border border-strong">
					<div className="text-xs text-subtle">Communities</div>
					<div className="text-lg font-semibold text-primary">{statistics.communities}</div>
				</div>

				<div className="space-y-1 p-3 rounded-md surface-muted border border-strong">
					<div className="text-xs text-subtle">Avg. Degree</div>
					<div className="text-lg font-semibold text-primary">{statistics.averageDegree.toFixed(2)}</div>
				</div>

				<div className="space-y-1 col-span-2 p-3 rounded-md surface-muted border border-strong">
					<div className="text-xs text-subtle">Density</div>
					<div className="text-lg font-semibold text-primary">{(statistics.density * 100).toFixed(2)}%</div>
				</div>
			</div>
		</div>
	);
};

export default StatisticsPanel;
